// Door attribution (#384).
//
// A "door" is a marketing landing page that owns one acquisition query class —
// /photo-sequence-to-video, /deflicker-timelapse, /lrtimelapse-alternative and
// the two device spokes under /guides. The door a visitor arrived through has
// to survive the whole conversion path —
// door CTA → sign-in → create project → Footage tab — so the GA4 funnel of one
// door can be compared against another's.
//
// Two rules this module exists to enforce:
//
//  1. DENY BY DEFAULT. `door` is a URL parameter, i.e. attacker-controlled, and
//     it ends up as a GA4 event param. Only values in DOORS are ever stored or
//     returned; anything else is dropped silently. That keeps arbitrary strings
//     out of analytics (cardinality + injection) and out of routing decisions.
//  2. ONE HOP, THEN CONSUMED. Attribution lives in sessionStorage, not the URL,
//     because the sign-in step is a FirebaseUI popup + a redirect into the
//     studio that would strip a query param. The first project created spends
//     it (clearDoor), so a later project in the same tab is not re-attributed.
//
// sessionStorage (not localStorage) on purpose: attribution is scoped to the
// visit that came through the door, and every access is wrapped because
// storage throws in Safari private mode / when cookies are blocked.

// Each constant is the wire value baked into a /studio?door=… URL and into GA4
// reports, so renaming one silently drops attribution for links already in the
// wild. Slugs match their page's route where the route has a natural short
// form, and are hyphenated lower-case throughout.
export const DOOR_PHOTO_SEQUENCE = 'photo-sequence';
export const DOOR_DEFLICKER = 'deflicker';
export const DOOR_LRTIMELAPSE_ALTERNATIVE = 'lrtimelapse-alternative';
export const DOOR_BRINNO_FLICKER = 'brinno-flicker';
export const DOOR_GOPRO_PHOTO_MODE = 'gopro-photo-mode';

// Allowlist. Add a door here in the same PR that ships its landing page.
export const DOORS = [
  DOOR_PHOTO_SEQUENCE,
  DOOR_DEFLICKER,
  DOOR_LRTIMELAPSE_ALTERNATIVE,
  DOOR_BRINNO_FLICKER,
  DOOR_GOPRO_PHOTO_MODE,
];

export const DOOR_PARAM = 'door';
export const DOOR_STORAGE_KEY = 'tweedia_door';

export function isKnownDoor(value) {
  return typeof value === 'string' && DOORS.includes(value);
}

function storage() {
  try {
    if (typeof sessionStorage === 'undefined' || sessionStorage === null) return null;
    return sessionStorage;
  } catch (_err) {
    return null;
  }
}

// Accepts anything a caller plausibly has on hand: URLSearchParams (or React
// Router's useSearchParams result), a raw query string, or a plain object.
function readParam(source) {
  if (!source) return null;
  if (typeof source.get === 'function') return source.get(DOOR_PARAM);
  if (typeof source === 'string') return new URLSearchParams(source).get(DOOR_PARAM);
  if (typeof source === 'object') return source[DOOR_PARAM];
  return null;
}

// Reads ?door= and persists it when (and only when) it is allowlisted.
//
// FIRST TOUCH WINS: a door already held for this session is never overwritten.
// That is the module's opening promise taken literally — the door a visitor
// ARRIVED through is the one that survives — and it started mattering with five
// that cross-link each other heavily (a spoke's whole job is to send readers to
// its hub). An unconditional write would re-attribute that visitor to whichever
// door they read second, and the bias runs one way, since spokes link to hubs
// and not the reverse, so spokes would systematically under-report. Attribution
// is released only by clearDoor / spendDoorOnProjectCreated.
//
// Returns the door now in effect — the incumbent one when a second door is
// visited — or null when there was nothing valid to capture.
export function captureDoor(searchParams) {
  const raw = readParam(searchParams);
  if (!isKnownDoor(raw)) return null;
  const incumbent = getDoor();
  if (incumbent) return incumbent;
  const store = storage();
  if (store) {
    try {
      store.setItem(DOOR_STORAGE_KEY, raw);
    } catch (_err) {
      // Storage full/blocked — attribution is best-effort, never a blocker.
    }
  }
  return raw;
}

// Re-validates on read: a value hand-planted in sessionStorage still has to be
// allowlisted before it can reach GA4 or a navigation decision.
export function getDoor() {
  const store = storage();
  if (!store) return null;
  try {
    const value = store.getItem(DOOR_STORAGE_KEY);
    return isKnownDoor(value) ? value : null;
  } catch (_err) {
    return null;
  }
}

export function clearDoor() {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(DOOR_STORAGE_KEY);
  } catch (_err) {
    // Nothing to do — a stale key only affects attribution, not behavior.
  }
}

// GA4 params for a step of the door funnel: door_view → cta_click → sign_up →
// project_created. Every door emits the SAME event names and differs only by
// this param, which is what lets one door's conversion be compared against
// another's in GA4 (#384 requirement). Returns {} for an unattributed visit so
// callers can spread it unconditionally without branching.
export function doorParams() {
  const door = getDoor();
  return door ? { door } : {};
}

// The funnel's terminal step. Attribution is spent here — the door earns credit
// for the FIRST project a visitor creates and nothing after it, so a user who
// keeps working in the same tab does not re-attribute every later project.
export function spendDoorOnProjectCreated() {
  const params = doorParams();
  if (params.door) clearDoor();
  return params;
}
